if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentZoom = 1;
        this.maxZoom = 3;
        this.minZoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.isDragging = false;
        this.lastX = 0;
        this.lastY = 0;
        this.setupZoomControls();
      }

      hide() {
        super.hide();
        this.resetZoom();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.resetZoom();
      }

      setupZoomControls() {
        // Add zoom controls to modal if they don't exist
        this.addEventListener('click', this.handleModalClick.bind(this));
        this.addEventListener('wheel', this.handleWheel.bind(this));
        this.addEventListener('mousedown', this.handleMouseDown.bind(this));
        this.addEventListener('mousemove', this.handleMouseMove.bind(this));
        this.addEventListener('mouseup', this.handleMouseUp.bind(this));
        this.addEventListener('mouseleave', this.handleMouseUp.bind(this));
        
        // Touch events for mobile
        this.addEventListener('touchstart', this.handleTouchStart.bind(this));
        this.addEventListener('touchmove', this.handleTouchMove.bind(this));
        this.addEventListener('touchend', this.handleTouchEnd.bind(this));
        
        // Keyboard events
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
      }

      createZoomControls() {
        const existingControls = this.querySelector('.zoom-controls');
        if (existingControls) return;

        const zoomControls = document.createElement('div');
        zoomControls.className = 'zoom-controls';
        zoomControls.innerHTML = `
          <button type="button" class="zoom-btn zoom-in" aria-label="Zoom in">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9 1v7H2v4h7v7h2v-7h7V8h-7V1H9z"/>
            </svg>
          </button>
          <button type="button" class="zoom-btn zoom-out" aria-label="Zoom out">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 8h16v4H2V8z"/>
            </svg>
          </button>
          <button type="button" class="zoom-btn zoom-reset" aria-label="Reset zoom" style="display: none;">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2a8 8 0 1 0 8 8h-2a6 6 0 1 1-6-6V2z"/>
              <path d="M10 0l4 4-4 4V0z"/>
            </svg>
          </button>
        `;

        const modalContent = this.querySelector('.product-media-modal__content');
        if (modalContent) {
          modalContent.appendChild(zoomControls);
        }

        // Add event listeners to zoom controls
        zoomControls.querySelector('.zoom-in').addEventListener('click', () => this.zoomIn());
        zoomControls.querySelector('.zoom-out').addEventListener('click', () => this.zoomOut());
        zoomControls.querySelector('.zoom-reset').addEventListener('click', () => this.resetZoom());
      }

      handleModalClick(event) {
        // Don't zoom if clicking on controls or close button
        if (event.target.closest('.zoom-controls') || 
            event.target.closest('.product-media-modal__toggle') ||
            event.target.closest('button')) {
          return;
        }

        const activeImage = this.querySelector('.active img');
        if (!activeImage) return;

        // Toggle zoom on click
        if (this.currentZoom === this.minZoom) {
          this.zoomTo(2, event.clientX, event.clientY);
        } else {
          this.resetZoom();
        }
      }

      handleWheel(event) {
        const activeImage = this.querySelector('.active img');
        if (!activeImage) return;

        event.preventDefault();
        const delta = event.deltaY > 0 ? -0.2 : 0.2;
        const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.currentZoom + delta));
        this.zoomTo(newZoom, event.clientX, event.clientY);
      }

      handleMouseDown(event) {
        const activeImage = this.querySelector('.active img');
        if (!activeImage || event.target.closest('.zoom-controls') || event.target.closest('.product-media-modal__toggle')) return;

        if (this.currentZoom > this.minZoom) {
          this.isDragging = true;
          this.lastX = event.clientX;
          this.lastY = event.clientY;
          activeImage.style.cursor = 'grabbing';
          event.preventDefault();
        }
      }

      handleMouseMove(event) {
        const activeImage = this.querySelector('.active img');
        if (!activeImage || !this.isDragging) return;

        const deltaX = event.clientX - this.lastX;
        const deltaY = event.clientY - this.lastY;
        this.panX += deltaX;
        this.panY += deltaY;
        this.lastX = event.clientX;
        this.lastY = event.clientY;
        this.updateImageTransform();
      }

      handleMouseUp() {
        const activeImage = this.querySelector('.active img');
        if (!activeImage) return;

        this.isDragging = false;
        activeImage.style.cursor = this.currentZoom > this.minZoom ? 'grab' : 'zoom-in';
      }

      handleTouchStart(event) {
        const activeImage = this.querySelector('.active img');
        if (!activeImage || event.target.closest('.zoom-controls')) return;

        if (event.touches.length === 1 && this.currentZoom > this.minZoom) {
          this.isDragging = true;
          this.lastX = event.touches[0].clientX;
          this.lastY = event.touches[0].clientY;
          event.preventDefault();
        }
      }

      handleTouchMove(event) {
        const activeImage = this.querySelector('.active img');
        if (!activeImage || !this.isDragging || event.touches.length !== 1) return;

        event.preventDefault();
        const deltaX = event.touches[0].clientX - this.lastX;
        const deltaY = event.touches[0].clientY - this.lastY;
        this.panX += deltaX;
        this.panY += deltaY;
        this.lastX = event.touches[0].clientX;
        this.lastY = event.touches[0].clientY;
        this.updateImageTransform();
      }

      handleTouchEnd() {
        this.isDragging = false;
      }

      handleKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        
        switch(event.key) {
          case '+':
          case '=':
            event.preventDefault();
            this.zoomIn();
            break;
          case '-':
            event.preventDefault();
            this.zoomOut();
            break;
          case '0':
            event.preventDefault();
            this.resetZoom();
            break;
        }
      }

      zoomTo(level, clientX = null, clientY = null) {
        const activeImage = this.querySelector('.active img');
        if (!activeImage) return;

        const oldZoom = this.currentZoom;
        this.currentZoom = Math.max(this.minZoom, Math.min(this.maxZoom, level));
        
        if (clientX !== null && clientY !== null) {
          const rect = activeImage.getBoundingClientRect();
          const x = clientX - rect.left - rect.width / 2;
          const y = clientY - rect.top - rect.height / 2;
          
          const zoomRatio = this.currentZoom / oldZoom;
          this.panX = (this.panX - x) * zoomRatio + x;
          this.panY = (this.panY - y) * zoomRatio + y;
        }
        
        this.updateImageTransform();
        this.updateCursor();
        this.updateZoomControls();
      }

      zoomIn() {
        this.zoomTo(Math.min(this.maxZoom, this.currentZoom + 0.5));
      }

      zoomOut() {
        this.zoomTo(Math.max(this.minZoom, this.currentZoom - 0.5));
      }

      resetZoom() {
        this.currentZoom = this.minZoom;
        this.panX = 0;
        this.panY = 0;
        this.updateImageTransform();
        this.updateCursor();
        this.updateZoomControls();
      }

      updateImageTransform() {
        const activeImage = this.querySelector('.active img');
        if (!activeImage) return;
        
        activeImage.style.transform = `scale(${this.currentZoom}) translate(${this.panX / this.currentZoom}px, ${this.panY / this.currentZoom}px)`;
        activeImage.style.transition = this.isDragging ? 'none' : 'transform 0.3s ease';
      }

      updateCursor() {
        const activeImage = this.querySelector('.active img');
        if (!activeImage) return;
        
        activeImage.style.cursor = this.currentZoom > this.minZoom ? 'grab' : 'zoom-in';
      }

      updateZoomControls() {
        const zoomInBtn = this.querySelector('.zoom-in');
        const zoomOutBtn = this.querySelector('.zoom-out');
        const resetBtn = this.querySelector('.zoom-reset');
        
        if (zoomInBtn) zoomInBtn.disabled = this.currentZoom >= this.maxZoom;
        if (zoomOutBtn) zoomOutBtn.disabled = this.currentZoom <= this.minZoom;
        if (resetBtn) resetBtn.style.display = this.currentZoom > this.minZoom ? 'block' : 'none';
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        // Create zoom controls for images
        const activeImage = activeMedia.querySelector('img');
        if (activeImage) {
          this.createZoomControls();
          this.updateZoomControls();
          this.updateCursor();
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
